import React from 'react';
import {
  Box,
  Card,
  CardBody,
  CardHeader,
  Heading,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Area,
  AreaChart,
} from 'recharts';

interface PriceChartProps {
  data: Array<{
    time: string;
    price: number;
  }>;
  isLoading?: boolean;
  t?: (key: string) => string; // Translation function
}

const PriceChart: React.FC<PriceChartProps> = ({ data, isLoading = false, t }) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const gridColor = useColorModeValue('gray.100', 'gray.600');

  // Default translations if no translation function provided
  const getText = (key: string) => {
    if (t) return t(key);
    const defaults: Record<string, string> = {
      'priceTrend.title': '24小时价格趋势',
      'priceTrend.description': '实时监控DOR价格与锚定线(1.00 USDT)的偏离情况',
      'priceTrend.priceLabel': 'DOR价格',
      'priceTrend.anchorLine': '锚定线 (1.00 USDT)',
      'priceTrend.loading': '价格趋势'
    };
    return defaults[key] || key;
  };

  if (isLoading) {
    return (
      <Card bg={bgColor} border="1px" borderColor={borderColor}>
        <CardHeader>
          <Heading size="md">{getText('priceTrend.loading')}</Heading>
        </CardHeader>
        <CardBody>
          <Box h="200px" bg="gray.100" borderRadius="md" />
        </CardBody>
      </Card>
    );
  }

  const formatTooltip = (value: any, name: string) => {
    if (name === 'price') {
      return [`${value} USDT`, getText('priceTrend.priceLabel')];
    }
    return [value, name];
  };

  const formatYAxis = (tickItem: number) => {
    return `${tickItem.toFixed(2)}`;
  };

  return (
    <Card bg={bgColor} border="1px" borderColor={borderColor}>
      <CardHeader>
        <Heading size="md">{getText('priceTrend.title')}</Heading>
        <Text fontSize="sm" color="gray.500">
          {getText('priceTrend.description')}
        </Text>
      </CardHeader>
      <CardBody>
        <Box h="200px">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
              <defs>
                <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3182CE" stopOpacity={0.3}/>
                  <stop offset="95%" stopColor="#3182CE" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={gridColor} />
              <XAxis 
                dataKey="time" 
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => {
                  // Handle time string format like "00:00", "02:00", etc.
                  if (typeof value === 'string' && value.includes(':')) {
                    return value;
                  }
                  // Handle Date objects
                  const date = new Date(value);
                  return date.toLocaleTimeString(t ? 'en-US' : 'zh-CN', { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  });
                }}
              />
              <YAxis 
                domain={['dataMin - 0.02', 'dataMax + 0.02']}
                tickFormatter={formatYAxis}
                tick={{ fontSize: 12 }}
              />
              <Tooltip 
                formatter={formatTooltip}
                labelFormatter={(label) => {
                  // Handle time string format like "00:00", "02:00", etc.
                  if (typeof label === 'string' && label.includes(':')) {
                    return `${getText('priceTrend.title')} - ${label}`;
                  }
                  // Handle Date objects
                  const date = new Date(label);
                  return date.toLocaleString(t ? 'en-US' : 'zh-CN');
                }}
              />
              {/* 锚定线 */}
              <ReferenceLine 
                y={1.00} 
                stroke="#38A169" 
                strokeDasharray="3 3"
                label={{ 
                  value: getText('priceTrend.anchorLine'), 
                  position: 'insideTopRight',
                  fill: '#38A169',
                  fontSize: 12
                }}
              />
              {/* 价格区域 */}
              <Area
                type="monotone"
                dataKey="price"
                stroke="#3182CE"
                strokeWidth={2}
                fill="url(#priceGradient)"
                dot={{ fill: '#3182CE', strokeWidth: 2, r: 3 }}
                activeDot={{ r: 5, stroke: '#3182CE', strokeWidth: 2 }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </Box>
      </CardBody>
    </Card>
  );
};

export default PriceChart; 